import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CContainer,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CSpinner,
  CAlert,
} from '@coreui/react'
import { cilArrowCircleLeft, cilFactory, cilReload, cilSave } from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import apiClient from '../../services/apiClient'
import { AOI_FULL_CONFIG, buildApiUrl } from '../../utils/getConfiguration'
import InternalAppHeader from './components/InternalAppHeader'
import { useTranslation } from 'react-i18next'
import { SplashScreen } from '@capacitor/splash-screen'

const GROUP_IMAGE_MAP = AOI_FULL_CONFIG.reduce((acc, config) => {
  if (config?.grp_ID && config?.image) {
    acc[String(config.grp_ID)] = config.image
  }
  return acc
}, {})

const DEFAULT_GROUP_IMAGE = AOI_FULL_CONFIG.find((config) => config?.grp_ID === 4)?.image

const AVATAR_SIZE = 64

const sanitizeValue = (value) => {
  if (!value) return ''
  const trimmed = String(value).trim()
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return ''
  return trimmed
}

const SuppliersSelection = () => {
  const { t } = useTranslation()
  const [formValues, setFormValues] = useState({
    supplierName: '',
    supplierContact: '',
    supplierPhone: '',
  })
  const [prefsuppliers, setPrefSuppliers] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }, [])

  const { supplierName, supplierContact, supplierPhone } = formValues

  const handleBack = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.history.go(-1)
    }
  }, [])

  const storageContext = useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        accountId: '',
        reminderId: '',
        supplierId: '',
        groupId: '',
      }
    }

    const normalize = (value) => {
      if (!value || value === 'null' || value === 'undefined') return ''
      return value
    }

    return {
      accountId: normalize(localStorage.getItem('account_ID')),
      reminderId: normalize(localStorage.getItem('taskid')),
      supplierId: normalize(localStorage.getItem('supplierid')),
      groupId: normalize(localStorage.getItem('grp_ID')),
    }
  }, [])

  const { accountId, reminderId, supplierId, groupId } = storageContext

  useEffect(() => {
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    if (window?.SplashScreen?.hide) {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) =>
        console.warn('Unable to hide splash screen', error),
      )
    }
  }, [])

  const groupImage = useMemo(
    () => GROUP_IMAGE_MAP[groupId] ?? DEFAULT_GROUP_IMAGE ?? AOI_FULL_CONFIG[0]?.image ?? '',
    [groupId],
  )

  const resolveSupplierImage = useCallback((supplier) => {
    if (!supplier) return ''
    const direct = sanitizeValue(supplier.pic)
    if (direct) return direct
    const picName = sanitizeValue(supplier.supplierPic)
    if (picName) {
      return `https://displatform.com/assets/suppliers/${picName}`
    }
    return ''
  }, [])

  const renderAvatar = useCallback(
    (overrideImage, alt = t('suppliersPage.avatarAlt', 'Supplier avatar')) => {
      const src = sanitizeValue(overrideImage) || sanitizeValue(groupImage)
      if (src) {
        return (
          <img
            src={src}
            alt={alt}
            width={AVATAR_SIZE}
            height={AVATAR_SIZE}
            style={{ borderRadius: '1rem', objectFit: 'cover' }}
            loading="lazy"
          />
        )
      }

      return (
        <div
          className="d-flex align-items-center justify-content-center shadow-sm"
          style={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            borderRadius: '1rem',
            background: 'rgba(94, 66, 255, 0.15)',
          }}
        >
          <CIcon icon={cilFactory} className="text-primary" size="lg" />
        </div>
      )
    },
    [groupImage],
  )

  const fetchPrefSuppliersData = useCallback(
    async (signal) => {
      if (!accountId || !reminderId || !groupId) return

      setIsFetching(true)
      setError(null)
      try {
        const requestConfig = signal ? { signal } : undefined
        const response = await apiClient.get(
          buildApiUrl('supplierPref', {
            reminder_ID: reminderId,
            account_ID: accountId,
            grp_ID: groupId,
          }),
          requestConfig,
        )

        setPrefSuppliers(Array.isArray(response.data) ? response.data : [])
      } catch (fetchError) {
        if (apiClient.isCancel?.(fetchError) || fetchError?.name === 'CanceledError') return
        console.error('Error fetching supplier', fetchError)
        setError(t('suppliers.errors.load'))
      } finally {
        setIsFetching(false)
      }
    },
    [accountId, reminderId, groupId, t],
  )

  const validateForm = useCallback(() => {
    if (!supplierName.trim()) {
      alert(t('suppliers.validation.name', 'Please enter supplier name'))
      return false
    }

    if (!supplierPhone.trim()) {
      alert(t('suppliers.validation.phone', 'Please enter supplier phone'))
      return false
    }

    return true
  }, [supplierName, supplierPhone, t])

  const submitAssignment = useCallback(
    async (endpointKey) => {
      if (!validateForm()) return
      if (!accountId || !reminderId) {
        alert(
          t('suppliers.validation.account', 'Unable to identify account. Please reload the page.'),
        )
        return
      }

      const params = {
        reminder_ID: reminderId,
        account_ID: accountId,
        supplier_name: supplierName.trim(),
        supplier_contact_name: supplierContact.trim(),
        supplier_contact_tel: supplierPhone.trim(),
      }

      if (supplierId) params.supplier_ID = supplierId
      if (endpointKey === 'supplierAssignManual' && groupId) params.grp_ID = groupId

      setIsSaving(true)
      setError(null)
      try {
        await apiClient.get(buildApiUrl(endpointKey, params))
        localStorage.setItem('suppliertel', supplierPhone.trim())
        await fetchPrefSuppliersData()
        setFormValues({
          supplierName: '',
          supplierContact: '',
          supplierPhone: '',
        })
        handleBack()
      } catch (submitError) {
        console.error('Error assigning supplier', submitError)
        setError(t('suppliers.errors.load'))
      } finally {
        setIsSaving(false)
      }
    },
    [
      accountId,
      fetchPrefSuppliersData,
      handleBack,
      reminderId,
      groupId,
      supplierContact,
      supplierId,
      supplierName,
      supplierPhone,
      validateForm,
      setFormValues,
    ],
  )

  const handleSubmit = useCallback(
    (event, endpoint) => {
      event.preventDefault()
      submitAssignment(endpoint)
    },
    [submitAssignment],
  )

  const renderSupplierFields = useCallback(
    (idPrefix) => {
      const nameId = `${idPrefix}-supplier-name`
      const contactId = `${idPrefix}-supplier-contact`
      const phoneId = `${idPrefix}-supplier-phone`

      return (
        <>
          <CCol xs={12} md={6}>
            <CFormLabel htmlFor={nameId}>{t('suppliersPage.fields.name')}</CFormLabel>
            <CFormInput
              type="text"
              id={nameId}
              name="supplierName"
              placeholder={t('suppliersPage.placeholders.company', 'Company name')}
              value={supplierName}
              onChange={handleInputChange}
              size="lg"
            />
          </CCol>
          <CCol xs={12} md={6}>
            <CFormLabel htmlFor={contactId}>{t('suppliersPage.fields.contact')}</CFormLabel>
            <CFormInput
              type="text"
              id={contactId}
              name="supplierContact"
              placeholder={t('suppliersPage.placeholders.contact', 'Contact name')}
              value={supplierContact}
              onChange={handleInputChange}
              size="lg"
            />
          </CCol>
          <CCol xs={12} md={6} lg={4}>
            <CFormLabel htmlFor={phoneId}>{t('suppliersPage.fields.phone')}</CFormLabel>
            <CFormInput
              type="text"
              id={phoneId}
              name="supplierPhone"
              placeholder={t('suppliersPage.placeholders.phone', '+xxxxxxxxxxxx')}
              value={supplierPhone}
              onChange={handleInputChange}
              size="lg"
            />
          </CCol>
        </>
      )
    },
    [handleInputChange, supplierContact, supplierName, supplierPhone],
  )

  const renderPreferredSupplierCard = useCallback(
    (prefsupplier, index = 0) => {
      const supplierImage = resolveSupplierImage(prefsupplier)
      const contactName = sanitizeValue(prefsupplier.supplier_contact_name)
      const contactTel = sanitizeValue(prefsupplier.supplier_contact_tel)
      const metaLines = [contactName, contactTel].filter(Boolean)

      return (
        <CForm
          key={prefsupplier.supplier_id || `pref-${index}`}
          onSubmit={(event) => handleSubmit(event, 'supplierAssign')}
        >
          <CCard className="border-0 shadow-sm mb-4">
            <CCardBody className="p-4 p-lg-5 d-flex flex-column gap-4">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-4">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  {renderAvatar(supplierImage, prefsupplier.supplier_name)}
                  <div className="d-flex flex-column gap-1">
                    <span className="text-uppercase small text-primary fw-semibold">
                      {t('suppliers.badges.preferredLabel', 'Preferred')}
                    </span>
                    <h4 className="mb-0">
                      {sanitizeValue(prefsupplier.supplier_name) ||
                        t('suppliers.fallbacks.preferred')}
                    </h4>
                    {metaLines.length > 0 && (
                      <div className="text-medium-emphasis small d-flex flex-column">
                        {metaLines.map((line, metaIndex) => (
                          <span key={`${prefsupplier.supplier_id}-meta-${metaIndex}`}>{line}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <CRow className="g-4">
                {renderSupplierFields(`pref-${prefsupplier.supplier_id || index}`)}
              </CRow>
              <div className="d-flex justify-content-end">
                <CButton
                  color="primary"
                  type="submit"
                  disabled={isSaving}
                  className="d-inline-flex align-items-center justify-content-center"
                  aria-label={t('suppliers.actions.setPreferred')}
                >
                  <CIcon icon={cilSave} size="lg" />
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        </CForm>
      )
    },
    [handleSubmit, isSaving, renderAvatar, renderSupplierFields, resolveSupplierImage],
  )

  const renderManualSupplierCard = useCallback(() => {
    if (isFetching) return null

    return (
      <CForm onSubmit={(event) => handleSubmit(event, 'supplierAssignManual')}>
        <CCard className="border-0 shadow-sm mb-4">
          <CCardBody className="p-4 p-lg-5 d-flex flex-column gap-4">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              {renderAvatar('', t('suppliersPage.avatarAlt', 'Group avatar'))}
              <div className="d-flex flex-column gap-1">
                <span className="text-uppercase small text-primary fw-semibold">
                  {t('suppliersPage.manual.pill')}
                </span>
                <h4 className="mb-0">{t('suppliersPage.manual.title')}</h4>
                <span className="text-medium-emphasis small">
                  {t('suppliersPage.manual.subtitle')}
                </span>
              </div>
            </div>
            <CRow className="g-4">{renderSupplierFields('manual')}</CRow>
            <div className="d-flex justify-content-end">
              <CButton
                color="primary"
                type="submit"
                disabled={isSaving}
                className="d-inline-flex align-items-center justify-content-center"
                aria-label={t('suppliers.actions.setPreferred')}
              >
                <CIcon icon={cilSave} size="lg" />
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CForm>
    )
  }, [handleSubmit, isFetching, isSaving, renderAvatar, renderSupplierFields])

  useEffect(() => {
    const controller = new AbortController()
    fetchPrefSuppliersData(controller.signal)
    return () => controller.abort()
  }, [fetchPrefSuppliersData])

  return (
    <div className="suppliers-selection-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader
          title={t('suppliersPage.header.title', 'New supplier')}
          onBack={handleBack}
        />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            <div className="d-flex flex-column gap-4">
              {isFetching && (
                <div className="d-flex align-items-center gap-2 text-medium-emphasis">
                  <CSpinner size="sm" color="primary" /> Loading preferred suppliers...
                </div>
              )}

              {error && (
                <CAlert color="danger" className="d-flex align-items-center gap-3">
                  <span className="fw-semibold flex-grow-1 mb-0">{error}</span>
                  <CButton
                    color="danger"
                    variant="outline"
                    size="sm"
                    onClick={() => fetchPrefSuppliersData()}
                    aria-label="Retry loading preferred suppliers"
                    disabled={isFetching}
                  >
                    <CIcon icon={cilReload} size="lg" />
                  </CButton>
                </CAlert>
              )}

              {prefsuppliers.length > 0
                ? [
                    ...prefsuppliers.map((supplier, index) =>
                      renderPreferredSupplierCard(supplier, index),
                    ),
                    renderManualSupplierCard(),
                  ]
                : !error && renderManualSupplierCard()}
            </div>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default SuppliersSelection
